import React, { useState, useEffect, useRef } from 'react';
import HLSPlayer from '../components/HLSPlayer';
import NavBar from '../components/NavBar';
import api from '../api';

export default function GuestStudio() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ guestName: '', mode: 'voice' });
  const [copied, setCopied] = useState('');
  const [previewRoom, setPreviewRoom] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/guest/rooms');
      setRooms(data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const createRoom = async () => {
    if (!form.guestName.trim()) { alert('Enter guest name'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/guest/rooms', form);
      setRooms(r => [data, ...r]);
      setForm({ guestName: '', mode: 'voice' });
    } catch { alert('Failed to create room'); }
    setCreating(false);
  };

  const startPreview = async (room) => {
    try {
      await api.post(`/guest/rooms/${room.roomId}/start-hls`);
      setPreviewRoom(room);
    } catch { alert('Failed to start preview — make sure guest is connected first'); }
  };

  const deleteRoom = async (roomId) => {
    if (!window.confirm('Close this guest room? The guest will be disconnected.')) return;
    await api.delete(`/guest/rooms/${roomId}`);
    setRooms(r => r.filter(x => x.roomId !== roomId));
    if (previewRoom?.roomId === roomId) setPreviewRoom(null);
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const s = {
    page: { padding: '28px 24px', maxWidth: '960px', margin: '0 auto' },
    title: { fontSize: '24px', fontWeight: '800', marginBottom: '4px' },
    sub: { color: 'var(--text-muted)', fontSize: '14px', marginBottom: '28px' },
    card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '24px', marginBottom: '16px' },
    label: { display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px' },
    input: { width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text)', fontSize: '14px', marginBottom: '12px' },
    btn: { padding: '10px 20px', borderRadius: '8px', border: 'none', background: 'var(--signal)', color: '#fff', fontWeight: '700', fontSize: '14px', cursor: 'pointer' },
    modeBtn: (active) => ({ flex: 1, padding: '8px', borderRadius: '8px', border: `1px solid ${active ? 'var(--signal)' : 'var(--border)'}`, background: active ? 'rgba(232,92,53,0.1)' : 'var(--surface2)', color: active ? 'var(--signal)' : 'var(--text-muted)', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }),
    urlBox: { background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 12px', fontSize: '12px', fontFamily: 'var(--mono)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' },
    copyBtn: (c) => ({ padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--border)', background: c ? 'rgba(0,200,100,0.1)' : 'var(--surface)', color: c ? '#00c864' : 'var(--text-muted)', fontSize: '11px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }),
  };

  return (
    <NavBar>
      <div style={s.page}>
        <div style={s.title}>🎙 Guest Studio</div>
        <div style={s.sub}>Invite guests to join your live stream via browser — no software needed</div>

        <div style={{ display: 'grid', gridTemplateColumns: previewRoom ? '1fr 1fr' : '1fr', gap: '16px' }}>
          <div>
            {/* Create Room */}
            <div style={s.card}>
              <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '16px' }}>New Guest Room</div>
              <div>
                <label style={s.label}>Guest Name</label>
                <input style={s.input} placeholder="e.g. DJ Marcus, Reporter Jane..." value={form.guestName}
                  onChange={e => setForm({...form, guestName: e.target.value})}
                  onKeyDown={e => e.key === 'Enter' && createRoom()} />
              </div>
              <div>
                <label style={s.label}>Audio Mode</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <button style={s.modeBtn(form.mode==='voice')} onClick={() => setForm({...form, mode:'voice'})}>🎤 Voice</button>
                  <button style={s.modeBtn(form.mode==='music')} onClick={() => setForm({...form, mode:'music'})}>🎵 Music</button>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '14px' }}>
                  {form.mode === 'music' ? 'Disables noise cancellation — for instruments or DJ sets' : 'Noise cancellation on — for interviews and call-ins'}
                </div>
              </div>
              <button style={s.btn} onClick={createRoom} disabled={creating}>
                {creating ? 'Creating…' : '+ Create Room'}
              </button>
            </div>

            {/* Active Rooms */}
            <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '12px' }}>
              Active Rooms {rooms.length > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: '400' }}>({rooms.length})</span>}
            </div>

            {loading ? (
              <div style={{ color: 'var(--text-muted)', padding: '20px 0' }}>Loading…</div>
            ) : rooms.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '14px', padding: '16px 0' }}>No active rooms.</div>
            ) : rooms.map(room => (
              <div key={room.roomId} style={s.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '14px' }}>
                      {room.guestName}
                      <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', background: 'rgba(232,92,53,0.1)', color: 'var(--signal)', border: '1px solid rgba(232,92,53,0.2)', marginLeft: '8px' }}>
                        {room.mode === 'music' ? '🎵 Music' : '🎤 Voice'}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {new Date(room.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <button
                    style={{ padding: '5px 12px', borderRadius: '6px', border: '1px solid rgba(220,38,38,0.3)', background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                    onClick={() => deleteRoom(room.roomId)}>
                    Close Room
                  </button>
                </div>

                <label style={s.label}>🔗 Guest Link</label>
                <div style={s.urlBox}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{room.guestUrl}</span>
                  <button style={s.copyBtn(copied===room.roomId+'g')} onClick={() => copy(room.guestUrl, room.roomId+'g')}>
                    {copied===room.roomId+'g' ? '✓' : 'Copy'}
                  </button>
                </div>

                <label style={s.label}>📡 RTMP Pull URL (OBS / Wirecast / vMix)</label>
                <div style={s.urlBox}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{room.pullUrl}</span>
                  <button style={s.copyBtn(copied===room.roomId+'r')} onClick={() => copy(room.pullUrl, room.roomId+'r')}>
                    {copied===room.roomId+'r' ? '✓' : 'Copy'}
                  </button>
                </div>

                <label style={s.label}>📺 HLS URL (VLC / browser preview)</label>
                <div style={s.urlBox}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{room.hlsUrl}</span>
                  <button style={s.copyBtn(copied===room.roomId+'h')} onClick={() => copy(room.hlsUrl, room.roomId+'h')}>
                    {copied===room.roomId+'h' ? '✓' : 'Copy'}
                  </button>
                </div>

                <div style={{ fontSize: '11px', color: 'var(--text-muted)', background: 'var(--surface2)', borderRadius: '8px', padding: '10px 12px', marginTop: '4px', lineHeight: '1.7' }}>
                  <strong style={{ color: 'var(--text)' }}>OBS:</strong> Sources → + → Media Source → uncheck Local File → paste RTMP URL<br/>
                  <strong style={{ color: 'var(--text)' }}>Wirecast:</strong> Add Shot → Network Stream → RTMP → paste RTMP URL<br/>
                  <strong style={{ color: 'var(--text)' }}>vMix:</strong> Add Input → Stream/SRT → paste RTMP URL<br/>
                  <strong style={{ color: 'var(--text)' }}>VLC:</strong> Media → Open Network Stream → paste HLS URL
                </div>

                <button
                  style={{ marginTop: '12px', padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--signal)', background: 'rgba(232,92,53,0.08)', color: 'var(--signal)', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
                  onClick={() => previewRoom?.roomId === room.roomId ? setPreviewRoom(null) : startPreview(room)}>
                  {previewRoom?.roomId === room.roomId ? '✕ Close Preview' : '▶ Preview Guest'}
                </button>
              </div>
            ))}
          </div>

          {/* Preview Panel */}
          {previewRoom && (
            <div>
              <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '12px' }}>
                👁 Previewing: {previewRoom.guestName}
              </div>
              <div style={s.card}>
                <HLSPlayer hlsUrl={previewRoom.hlsUrl} live={true} streamKey={previewRoom.streamKey} serverHost={window.location.host} />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '10px' }}>
                  Live preview via HLS — ~5 second delay from source.<br/>
                  Use RTMP Pull URL in OBS/Wirecast for lower latency.
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '8px', padding: '16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px', fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.8' }}>
          <strong style={{ color: 'var(--text)' }}>How it works:</strong> Share the Guest Link → Guest opens in browser → clicks Go Live → 
          copy RTMP Pull URL into OBS/Wirecast as a media source → mix with your feed → broadcast normally.<br/>
          <strong style={{ color: 'var(--text)' }}>Latency:</strong> ~200-400ms guest → server (like a phone call) · RTMP pull adds ~1-2s more
        </div>
      </div>
    </NavBar>
  );
}
